import Canvas from "./Canvas";

import { connect } from "react-redux";
import compose from "recompose/compose";
import lifecycle from "recompose/lifecycle";
import mapProps from "recompose/mapProps";

import omit from "lodash/omit";
import isEmpty from "lodash/isEmpty";
import has from "lodash/has";
import set from "lodash/set";

import addPrices from "src/common/utils/add-prices";
import mergeObjects from "src/common/utils/merge-objects";
import { findVariantPriceForProduct } from "src/common/utils/product";
import { replaceTextWithValues } from "src/common/utils/text-replace";

import { withModel } from "../../data-providers/model";
import withCanvasPrimary from "./withCanvasPrimary";

import { getActiveCanvasDrawer } from "../CanvasDrawers/duck";
import { addFlashMessageNotice } from "../FlashMessages/duck";
import { fetchPrismicProduct } from "src/client/js/store/thunks/prismic/product";
import {
  getPrismicProduct,
  getPrismicProductByLocale,
  getPrismicProductDrawer,
} from "src/client/js/store/selectors/prismic/product";

import {
  getDefaultCustomisations,
  replaceCanvasText,
  mergeLocalisedDrawers,
  getCompletedDrawersMap,
  shouldInscriptionUpdate,
  shouldFetchPrismicProduct,
} from "./helpers";
import withProduct from "../../hocs/withProduct";
import withProducts from "../../hocs/withProducts";
import withQueryParams from "../../hocs/withQueryParams";

const buildTotalPrice = (props) => {
  const formatPrice = findVariantPriceForProduct(
    props.product,
    omit(props.productCustomisation, ["box", "cover", "components"]),
    props.model.countryModel
  );

  if (!formatPrice || isEmpty(props.productVariant)) {
    return formatPrice;
  }

  const upsellCost = (props.productCustomisation.components || []).reduce(
    (acc, { id: customisationComponentId }) => {
      const upselledOption = props.productVariant.components.find(
        ({ id: productComponentId }) =>
          customisationComponentId === productComponentId
      );

      if (!upselledOption) {
        return acc;
      }

      return acc + upselledOption.price.amount;
    },
    0
  );

  return addPrices(formatPrice, upsellCost);
};

const enhance = compose(
  withProduct,
  withProducts,
  withQueryParams,
  withModel,
  withCanvasPrimary,
  connect(
    (state, props) => ({
      isDrawerOpen: !!getActiveCanvasDrawer(state),
      inscriptionDrawer: getPrismicProductDrawer(
        state,
        props.primary.productId,
        props.productCustomisation.locale,
        "inscription"
      ),
      prismicProductLangs: getPrismicProduct(state, props.primary.productId),
      prismicProduct: getPrismicProductByLocale(
        state,
        props.primary.productId,
        props.productCustomisation.locale
      ),
    }),
    (dispatch) => ({
      flashMessage: (message) =>
        message && dispatch(addFlashMessageNotice(message)),
    })
  ),
  connect(null, (dispatch, props) => ({
    fetchPrismicProduct: (sku, locale) =>
      dispatch(fetchPrismicProduct(sku, locale, props)),
  })),
  mapProps((props) => {
    const isEditing = !!props.match.params.itemId;
    let cta = isEditing ? props.primary.editingCta : props.primary.cta;
    let completedCta = isEditing
      ? props.primary.completedEditingCta
      : props.primary.completedCta;
    const localisedDrawers = mergeLocalisedDrawers(props);
    const propsWithDrawers = {
      ...props,
      drawers:
        localisedDrawers.length > 0
          ? { ...props.primary.drawers, other: localisedDrawers }
          : props.primary.drawers,
    };
    const completedDrawersMap = getCompletedDrawersMap(propsWithDrawers);

    return {
      ...propsWithDrawers,
      completedDrawersMap,
      isDrawersVisible: props.primary.body.length > 0 || !!cta,
      title: replaceCanvasText(props.primary.title, props),
      editingBanner: props.primary.editingBanner,
      continueCtaText: cta,
      completedCtaText: replaceTextWithValues(completedCta || cta, {
        price: buildTotalPrice(props),
      }),
    };
  }),
  lifecycle({
    componentDidMount() {
      const props = this.props;

      // Drawer that sets defuault value for a particular key - this is set in the CMS and is a fixed value
      // for that key (so slightly different from a default value)
      const hiddenStaticCustomistaion = props.drawers.other
        .filter((drawer) => drawer.sliceType === "hidden_default_value")
        .reduce((obj, drawer) => {
          if (!has(props.queryParams, drawer.primary.drawerKey)) {
            set(obj, drawer.primary.drawerKey, drawer.primary.defaultValue);
          }
          return obj;
        }, {});

      // We need to set the hidden drawers params in the url as it a value that
      // needs to get passed through to following steps as a fixed value.
      // Replacing query params instead as it pushes the new query onto the url without updating the history
      props.replaceQueryParams(hiddenStaticCustomistaion);

      // We want to set all default values as customisation so users don't have to edit and save all drawers.
      // Although the original functionality of editing individual drawers and saving the changes remain.
      // Find any drawers which are multi_level and need to be prefilled
      const multiLevelFormDrawers = props.drawers.other.filter((drawer) => {
        return (
          drawer.sliceType === "multi_level_form" &&
          drawer.primary.usePrefillValues
        );
      });

      if (multiLevelFormDrawers.length > 0) {
        const paramsToAppend = {};

        for (const drawer of multiLevelFormDrawers) {
          for (const item of drawer.items) {
            const fields = item.form.body.filter((b) => {
              // Discard form fields without a prefilledValue or where param already exists in url
              return !isEmpty(b.prefillValue) && !props.queryParams[b.name];
            });

            for (const field of fields) {
              // Replace dynamic strings in field text with existing set customisations
              // (e.g. '%{name}' -> 'Dave').
              // Then add to paramsToAppend as { key: value }.
              paramsToAppend[field.name] = replaceCanvasText(
                field.prefillValue,
                props
              );
            }
          }
        }

        props.replaceQueryParams(paramsToAppend);
      }

      const customisation = mergeObjects(
        getDefaultCustomisations(props),
        hiddenStaticCustomistaion,
        props.queryParams
      );

      props.updateProductCustomisation(customisation);

      if (props.match.params.itemId) {
        const canvasFlashMessage = replaceCanvasText(props.editingBanner, {
          ...props,
          productCustomisation: customisation,
        });
        props.flashMessage(canvasFlashMessage);
      }

      this.props.fetchPrismicProduct(
        props.primary.productId,
        customisation.locale
      );
    },

    componentDidUpdate(prevProps) {
      const props = this.props;

      if (shouldInscriptionUpdate(prevProps, props)) {
        // if inscription updates other things that replace text should also update
        // e.g. multi forms.  For safety we get all the customisations
        const customisation = mergeObjects(
          getDefaultCustomisations(props),
          props.queryParams
        );
        prevProps.updateProductCustomisation(customisation);
      }

      if (shouldFetchPrismicProduct(prevProps, props)) {
        prevProps.fetchPrismicProduct(
          props.primary.productId,
          props.productCustomisation.locale
        );
      }
    },
  })
);

export default enhance(Canvas);
